import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BookOpen, Building2, Database, Download, History, ListChecks, Loader2, Mail, Pencil, Plus, RefreshCw, Save, Send, ShieldCheck, Type, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { MASTER_TYPES } from "@/lib/constants";
import { useCompanySettings } from "@/context/SettingsContext";

const emptyDraft = { label: "", field_type: "text", master_type: "", required: false, active: true };

/* ---------------- Company Settings tab ---------------- */

const COMPANY_FIELDS = [
  { key: "company_name", label: "Company Name" },
  { key: "contact_number", label: "Contact Number" },
  { key: "support_email", label: "Support Email" },
  { key: "support_whatsapp_number", label: "Support WhatsApp Number", hint: "Used by every 'Need Help? Chat with Us' button across the website. Include country code, digits only (e.g. 919876543210)." },
  { key: "office_hours", label: "Office Hours" },
  { key: "facebook_url", label: "Facebook URL" },
  { key: "instagram_url", label: "Instagram URL" },
  { key: "linkedin_url", label: "LinkedIn URL" },
  { key: "youtube_url", label: "YouTube URL" },
];

const CompanySettingsTab = () => {
  const { refreshSettings } = useCompanySettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setForm(res.data)).catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", form);
      refreshSettings();
      toast.success("Company settings saved — updated across the whole website");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return <Skeleton className="h-72 rounded-xl" />;
  }

  return (
    <Card className="rounded-xl border-border shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" /> Company Information
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Powers the website footer, Contact Us page and the floating WhatsApp chat button. Nothing is hardcoded — changes apply instantly everywhere.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {COMPANY_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input
                data-testid={`company-setting-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
              />
              {f.hint && <p className="text-[11px] text-muted-foreground">{f.hint}</p>}
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Office Address</Label>
            <Textarea
              data-testid="company-setting-office_address"
              rows={2}
              value={form.office_address || ""}
              onChange={(e) => setForm((s) => ({ ...s, office_address: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Footer Tagline</Label>
            <Textarea
              data-testid="company-setting-footer_tagline"
              rows={2}
              value={form.footer_tagline || ""}
              onChange={(e) => setForm((s) => ({ ...s, footer_tagline: e.target.value }))}
            />
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-lg" data-testid="company-settings-save-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Company Settings
        </Button>
      </CardContent>
    </Card>
  );
};

/* ---------------- Form Builder tab ---------------- */

const FormBuilderTab = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/form-fields", { params: { include_inactive: true } })
      .then((res) => setFields(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft);
    setDialogOpen(true);
  };

  const openEdit = (field) => {
    setEditing(field);
    setDraft({
      label: field.label,
      field_type: field.field_type,
      master_type: field.master_type || "",
      required: !!field.required,
      active: field.active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!draft.label.trim()) {
      toast.error("Field label is required");
      return;
    }
    if (draft.field_type === "dropdown" && !draft.master_type) {
      toast.error("Select a Master Data source for the dropdown");
      return;
    }
    setBusy(true);
    const payload = {
      label: draft.label.trim(),
      field_type: draft.field_type,
      master_type: draft.field_type === "dropdown" ? draft.master_type : null,
      required: draft.required,
      active: draft.active,
    };
    try {
      if (editing) {
        await api.put(`/form-fields/${editing.id}`, payload);
        toast.success(`Field "${payload.label}" updated`);
      } else {
        await api.post("/form-fields", payload);
        toast.success(`Field "${payload.label}" added`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (field) => {
    try {
      await api.put(`/form-fields/${field.id}`, {
        label: field.label,
        field_type: field.field_type,
        master_type: field.master_type || null,
        required: !!field.required,
        active: !(field.active !== false),
      });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const masterLabel = (key) => MASTER_TYPES.find((t) => t.key === key)?.label || key;

  if (loading) {
    return <Skeleton className="h-64 rounded-xl" />;
  }

  return (
    <>
      <Card className="rounded-xl border-border shadow-none">
        <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base font-semibold">Recruitment Profile Fields</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Fields recruiters fill after speaking to a member. Add text fields or dropdowns powered by Master Data.
            </p>
          </div>
          <Button onClick={openCreate} size="sm" className="rounded-lg shrink-0" data-testid="form-builder-add-field-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add Field
          </Button>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="form-builder-fields-list">
          {fields.length === 0 && (
            <EmptyState
              title="No custom fields yet"
              description="Add your first field to shape the recruitment profile form."
            />
          )}
          {fields.map((field) => (
            <div
              key={field.id}
              className="flex items-center gap-3 rounded-lg border border-border px-4 py-3"
              data-testid="form-builder-field-item"
            >
              <span className="h-8 w-8 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                {field.field_type === "dropdown" ? <ListChecks className="h-4 w-4" /> : <Type className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${field.active === false ? "text-muted-foreground line-through" : ""}`}>
                  {field.label}
                </p>
                <p className="text-xs text-muted-foreground">
                  {field.field_type === "dropdown" ? `Dropdown · ${masterLabel(field.master_type)}` : "Text field"}
                </p>
              </div>
              {field.required && (
                <Badge variant="outline" className="rounded-full text-[10px]">Required</Badge>
              )}
              {field.active === false && (
                <Badge variant="outline" className="rounded-full text-[10px] bg-muted">Inactive</Badge>
              )}
              <Switch
                checked={field.active !== false}
                onCheckedChange={() => toggleActive(field)}
                aria-label="Toggle field active"
                data-testid={`form-builder-toggle-${field.id}`}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={() => openEdit(field)}
                aria-label="Edit field"
                data-testid={`form-builder-edit-${field.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="form-builder-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Field" : "Add Field"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="field-label">Field Label</Label>
              <Input
                id="field-label"
                data-testid="form-builder-label-input"
                placeholder="e.g. Current CTC"
                value={draft.label}
                onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Field Type</Label>
              <Select
                value={draft.field_type}
                onValueChange={(v) => setDraft((d) => ({ ...d, field_type: v }))}
              >
                <SelectTrigger data-testid="form-builder-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="dropdown">Dropdown (Master Data)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.field_type === "dropdown" && (
              <div className="space-y-1.5">
                <Label>Master Data Source</Label>
                <Select
                  value={draft.master_type}
                  onValueChange={(v) => setDraft((d) => ({ ...d, master_type: v }))}
                >
                  <SelectTrigger data-testid="form-builder-master-select">
                    <SelectValue placeholder="Select master data group" />
                  </SelectTrigger>
                  <SelectContent>
                    {MASTER_TYPES.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium">Required field</p>
                <p className="text-xs text-muted-foreground">Recruiters must fill this before saving</p>
              </div>
              <Switch
                checked={draft.required}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, required: v }))}
                data-testid="form-builder-required-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDialogOpen(false)} data-testid="form-builder-cancel-button">
              Cancel
            </Button>
            <Button onClick={save} disabled={busy} className="rounded-lg" data-testid="form-builder-save-button">
              {editing ? "Save Changes" : "Add Field"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ---------------- System & Backups tab (M15) ---------------- */

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return `${new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Kolkata",
    })} IST`;
  } catch {
    return iso;
  }
};

const EMAIL_STATUS_LABEL = {
  sent: { text: "Emailed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  summary_sent: { text: "Summary emailed (file too large)", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  failed: { text: "Email failed", cls: "bg-red-50 text-red-700 border-red-200" },
  skipped: { text: "Email skipped", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const SystemBackupsTab = () => {
  const [status, setStatus] = useState(null);
  const [mailerOn, setMailerOn] = useState(null);
  const [files, setFiles] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runBusy, setRunBusy] = useState(false);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // true | false | null
  const [downloading, setDownloading] = useState("");
  const fileRef = useRef(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoreWord, setRestoreWord] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [s, f, m] = await Promise.all([
        api.get("/backups/status"),
        api.get("/backups/files"),
        api.get("/lineup-board/status"),
      ]);
      setStatus(s.data);
      setFiles(f.data.daily || []);
      setWeekly(f.data.weekly || []);
      setMailerOn(!!m.data.mailer_enabled);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunBusy(true);
    try {
      const res = await api.post("/backups/run");
      const em = res.data.email?.status;
      toast.success(
        `Backup created — ${res.data.documents} documents (${fmtSize(res.data.size_bytes)})` +
        (em === "sent" ? ". A copy was emailed to the Udhyog Tech Gmail." : ""));
      if (em === "failed") toast.warning("Backup email could not be sent — download the file below and store it safely.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunBusy(false);
    }
  };

  const download = async (file) => {
    setDownloading(file);
    try {
      const res = await api.get(`/backups/download/${file}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloading — store the file somewhere safe");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading("");
    }
  };

  const applySwitch = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    setSwitchBusy(true);
    try {
      const res = await api.put("/lineup-board/mailer-switch", { enabled: target });
      setMailerOn(!!res.data.mailer_enabled);
      toast.success(res.data.mailer_enabled
        ? "Auto-mailer is now ON — lineup emails send at 11:15 IST + 15-minute batches"
        : "Auto-mailer is now OFF — no lineup emails will be sent");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSwitchBusy(false);
    }
  };

  const pickRestoreFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && !f.name.endsWith(".json.gz")) {
      toast.error("Please choose a backup file downloaded from this page (.json.gz)");
      e.target.value = "";
      setRestoreFile(null);
      return;
    }
    setRestoreFile(f);
  };

  const doRestore = async () => {
    if (!restoreFile || restoreWord !== "RESTORE") return;
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const res = await api.post("/backups/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(
        `Database restored — ${res.data.collections_restored} collections, ` +
        `${res.data.documents_restored} documents. Safety backup: ${res.data.safety_backup_file}`);
      setRestoreDialog(false);
      setRestoreWord("");
      setRestoreFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Restore failed — the database was not changed."));
    } finally {
      setRestoreBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-96 rounded-xl" />;
  if (error) {
    return (
      <Card className="rounded-xl border-border shadow-none">
        <CardContent className="p-10 text-center" data-testid="system-tab-error">
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <Button variant="outline" onClick={load}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const lastBackup = status?.last_backup;
  const lastEmail = status?.last_email;
  const emailBadge = lastEmail ? EMAIL_STATUS_LABEL[lastEmail.status] || EMAIL_STATUS_LABEL.skipped : null;

  return (
    <div className="space-y-4" data-testid="system-backups-tab">
      {/* -------- Lineup auto-mailer switch -------- */}
      <Card className="rounded-xl border-border shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" /> Lineup Auto-Mailer
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Master switch for automatic lineup emails (11:15 AM IST + 15-minute batches). Kept OFF until you
            are ready to go live — recruiters can keep confirming "Going" safely; nothing is sent or lost while OFF.
          </p>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between rounded-lg border px-4 py-3 ${mailerOn ? "border-emerald-200 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`}>
            <div className="flex items-center gap-3">
              {mailerOn
                ? <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                : <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />}
              <div>
                <p className="text-sm font-semibold" data-testid="mailer-switch-state">
                  Auto-mailer is {mailerOn ? "ON" : "OFF"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {mailerOn
                    ? "Lineup emails go out automatically to company recipients."
                    : "No lineup emails are sent — automatic and manual sending are both blocked."}
                </p>
              </div>
            </div>
            <Switch
              checked={!!mailerOn}
              disabled={switchBusy}
              onCheckedChange={(v) => setConfirmTarget(v)}
              aria-label="Toggle lineup auto-mailer"
              data-testid="mailer-switch-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* -------- Backups -------- */}
      <Card className="rounded-xl border-border shadow-none">
        <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0 gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" /> Database Backups
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Full backup runs automatically every day at 11:00 PM IST. A copy is emailed to the Udhyog Tech
              Gmail, and you can download any backup below. The first backup of each week is also kept as a
              weekly archive for 3 months, so older recovery points stay available.
            </p>
          </div>
          <Button size="sm" className="rounded-lg shrink-0 gap-1.5" onClick={runNow} disabled={runBusy} data-testid="backup-run-now">
            {runBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Run backup now
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-border px-4 py-3" data-testid="backup-last-run">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Last backup</p>
              <p className="text-sm font-semibold mt-0.5">{lastBackup ? fmtWhen(lastBackup.at) : "Not yet run"}</p>
              {lastBackup && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {lastBackup.documents} documents · {fmtSize(lastBackup.size_bytes)}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-border px-4 py-3" data-testid="backup-next-run">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Next scheduled</p>
              <p className="text-sm font-semibold mt-0.5">{fmtWhen(status?.next_run_at)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{status?.schedule} · keeps last {status?.keep_last}</p>
            </div>
            <div className="rounded-lg border border-border px-4 py-3" data-testid="backup-email-status">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email copy
              </p>
              {emailBadge ? (
                <>
                  <Badge variant="outline" className={`rounded-full mt-1 ${emailBadge.cls}`}>{emailBadge.text}</Badge>
                  <p className="text-xs text-muted-foreground mt-1 truncate">to {lastEmail?.to || "—"}</p>
                </>
              ) : (
                <p className="text-sm font-semibold mt-0.5">—</p>
              )}
            </div>
          </div>

          {status?.last_error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2" data-testid="backup-error-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Last scheduled backup failed — please run a manual backup and download a copy.
            </div>
          )}

          {files.length === 0 && weekly.length === 0 ? (
            <EmptyState
              title="No backups yet"
              description="Run your first backup now — it takes a few seconds and emails a copy automatically."
            />
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table data-testid="backup-files-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Backup file</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...files, ...weekly].map((f) => (
                    <TableRow key={f.file} data-testid="backup-file-row">
                      <TableCell className="font-mono text-xs">{f.file}</TableCell>
                      <TableCell>
                        {f.kind === "weekly" ? (
                          <Badge variant="outline" className="rounded-full text-[10px] bg-violet-50 text-violet-700 border-violet-200" data-testid="backup-kind-weekly">
                            Weekly archive
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="rounded-full text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-testid="backup-kind-daily">
                            Daily
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{fmtWhen(f.modified_at)}</TableCell>
                      <TableCell className="text-sm">{fmtSize(f.size_bytes)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 rounded-lg"
                          disabled={downloading === f.file}
                          onClick={() => download(f.file)}
                          data-testid={`backup-download-${f.file}`}
                        >
                          {downloading === f.file
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Download className="h-3.5 w-3.5" />}
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* -------- Restore from backup -------- */}
      <Card className="rounded-xl border-border shadow-none">
        <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0 gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> Restore from Backup
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Load the database back from a downloaded backup file. Before anything changes, a safety
              backup of the current data is created automatically — so a restore can always be undone.
            </p>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg shrink-0 gap-1.5"
                  onClick={() => setGuideOpen(true)} data-testid="restore-guide-open">
            <BookOpen className="h-3.5 w-3.5" /> Restore drill guide
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".gz,application/gzip"
              className="hidden"
              onChange={pickRestoreFile}
              data-testid="restore-file-input"
            />
            <Button
              variant="outline"
              className="rounded-lg gap-1.5"
              onClick={() => fileRef.current?.click()}
              data-testid="restore-choose-file"
            >
              <Upload className="h-4 w-4" /> Choose backup file
            </Button>
            {restoreFile && (
              <span className="text-xs font-mono text-muted-foreground truncate max-w-xs" data-testid="restore-selected-file">
                {restoreFile.name}
              </span>
            )}
            <Button
              variant="destructive"
              className="rounded-lg gap-1.5"
              disabled={!restoreFile}
              onClick={() => { setRestoreWord(""); setRestoreDialog(true); }}
              data-testid="restore-open-dialog"
            >
              <History className="h-4 w-4" /> Restore…
            </Button>
          </div>
          {status?.last_restore && (
            <p className="text-xs text-muted-foreground" data-testid="backup-last-restore">
              Last restore: {fmtWhen(status.last_restore.at)} from{" "}
              <span className="font-mono">{status.last_restore.source_file}</span> by {status.last_restore.restored_by}
              {" — "}safety backup <span className="font-mono">{status.last_restore.safety_backup_file}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* -------- Restore drill guide (one page) -------- */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="restore-guide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" /> Restore Drill Guide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Follow these steps if data is ever lost, damaged or wrongly changed. Only Admins can restore.
            </p>
            <ol className="space-y-3 list-none">
              {[
                ["Pause data entry", "Ask the team to stop entering data so nothing new is lost during the restore."],
                ["Pick your recovery point", "In this tab, find the backup taken just BEFORE the problem. Daily backups cover the last 14 days; Weekly archives go back about 3 months. The nightly email copy in the Udhyog Tech Gmail also works."],
                ["Download the backup file", "Click Download next to the chosen backup and save the .json.gz file to your computer. If using the email copy, save the attachment."],
                ["Run the restore", "Under Restore from Backup: Choose backup file → select the saved file → Restore… → read the warning → type RESTORE → Restore now."],
                ["What happens automatically", "A safety backup of the current data is created first, the database is replaced with the backup contents, the auto-mailer switch keeps its position, all admins are notified, and the restore is recorded in the activity log."],
                ["Verify the result", "Check the dashboard numbers and open a few recent candidates/applications to confirm the data looks right."],
                ["Made a mistake?", "Restore again using the safety backup file (named in the confirmation message and in the 'Last restore' line below) — it contains everything exactly as it was just before your restore."],
              ].map(([title, body], i) => (
                <li key={title} className="flex gap-3" data-testid={`restore-guide-step-${i + 1}`}>
                  <span className="h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Anything entered AFTER the chosen backup will disappear and must be re-entered. When unsure which
              backup to use, download a fresh backup of the current state first, then ask for help before restoring.
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-lg" onClick={() => setGuideOpen(false)} data-testid="restore-guide-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Mailer switch confirmation -------- */}
      <Dialog open={confirmTarget !== null} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="mailer-switch-dialog">
          <DialogHeader>
            <DialogTitle>
              Turn auto-mailer {confirmTarget ? "ON" : "OFF"}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirmTarget
              ? "Lineup emails will start going out automatically to company recipients — at 11:15 AM IST and then every 15 minutes for new Going confirmations. Make sure company To/CC addresses are set correctly in Daily Lineup → Email settings."
              : "All lineup email sending stops immediately (automatic and manual). Going confirmations keep queueing safely and will send after you switch it back ON."}
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirmTarget(null)} data-testid="mailer-switch-cancel">
              Cancel
            </Button>
            <Button className="rounded-lg" onClick={applySwitch} data-testid="mailer-switch-confirm">
              {confirmTarget ? "Turn ON" : "Turn OFF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Restore confirmation (typed) -------- */}
      <Dialog open={restoreDialog} onOpenChange={(o) => { if (!o && !restoreBusy) { setRestoreDialog(false); setRestoreWord(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Restore database?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This will replace <span className="font-semibold text-foreground">all current data</span> with
              the contents of <span className="font-mono text-xs">{restoreFile?.name}</span>. Anything entered
              after that backup was taken will disappear.
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
              <li>A safety backup of today's data is created automatically first — you can restore it to undo.</li>
              <li>The lineup auto-mailer switch keeps its current position (it will not turn ON by itself).</li>
              <li>All admins are notified and the restore is recorded in the activity log.</li>
            </ul>
            <div className="space-y-1.5">
              <Label htmlFor="restore-word">Type <span className="font-mono font-semibold">RESTORE</span> to confirm</Label>
              <Input
                id="restore-word"
                value={restoreWord}
                onChange={(e) => setRestoreWord(e.target.value.toUpperCase())}
                placeholder="RESTORE"
                autoComplete="off"
                data-testid="restore-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" disabled={restoreBusy}
                    onClick={() => { setRestoreDialog(false); setRestoreWord(""); }} data-testid="restore-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-lg gap-1.5"
              disabled={restoreWord !== "RESTORE" || restoreBusy}
              onClick={doRestore}
              data-testid="restore-confirm-btn"
            >
              {restoreBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
              {restoreBusy ? "Restoring…" : "Restore now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Page ---------------- */

export default function AdminSettings() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = ["company", "form-builder", "system"].includes(tabParam) ? tabParam : "company";
  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Admin Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Configure company information and the recruitment profile form — no code changes needed.
        </p>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="grid grid-cols-3 w-full sm:w-[34rem]">
          <TabsTrigger value="company" data-testid="settings-tab-company">Company Settings</TabsTrigger>
          <TabsTrigger value="form-builder" data-testid="settings-tab-form-builder">Form Builder</TabsTrigger>
          <TabsTrigger value="system" data-testid="settings-tab-system">System & Backups</TabsTrigger>
        </TabsList>
        <TabsContent value="company" className="mt-4">
          <CompanySettingsTab />
        </TabsContent>
        <TabsContent value="form-builder" className="mt-4">
          <FormBuilderTab />
        </TabsContent>
        <TabsContent value="system" className="mt-4">
          <SystemBackupsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
